'use client';

import { useId } from 'react';

/**
 * SVG star with a horizontal-gradient fill from active (#7B4BF6) to inactive
 * (#ccc) at `value * 100%`. Mirrors RN `StarIcon` — lets a star render a
 * partial fill for fractional average scores.
 */
function StarIcon({ value, size = 21 }: { value: number; size?: number }) {
  const id = useId();
  const offset = `${(value * 100).toFixed(0)}%`;
  const height = (20 / 21) * size;
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 21 20"
      fill="none"
      aria-hidden
    >
      <defs>
        <linearGradient id={id}>
          <stop offset={offset} stopColor="#7B4BF6" />
          <stop offset={offset} stopColor="#ccc" />
        </linearGradient>
      </defs>
      <path
        d="M18.8039 8.97274L15.2882 12.0063L16.3593 16.5431C16.4184 16.7893 16.4032 17.0476 16.3156 17.2852C16.228 17.5229 16.0719 17.7292 15.8671 17.8782C15.6623 18.0272 15.4179 18.1121 15.1648 18.1222C14.9118 18.1324 14.6614 18.0673 14.4453 17.9352L10.5 15.5071L6.5523 17.9352C6.33622 18.0666 6.08613 18.131 5.8335 18.1205C5.58087 18.11 5.337 18.0249 5.13261 17.8761C4.92822 17.7272 4.77243 17.5212 4.68488 17.284C4.59732 17.0468 4.58191 16.789 4.64058 16.5431L5.71558 12.0063L2.19995 8.97274C2.00878 8.80752 1.87052 8.58963 1.80244 8.34629C1.73436 8.10296 1.73947 7.84496 1.81715 7.60452C1.89483 7.36407 2.04162 7.15184 2.23919 6.99432C2.43677 6.83681 2.67637 6.74099 2.92808 6.71884L7.53745 6.34696L9.31558 2.04384C9.41182 1.80932 9.57563 1.60872 9.78618 1.46754C9.99673 1.32636 10.2445 1.25098 10.498 1.25098C10.7515 1.25098 10.9993 1.32636 11.2098 1.46754C11.4204 1.60872 11.5842 1.80932 11.6804 2.04384L13.4578 6.34696L18.0671 6.71884C18.3193 6.74017 18.5596 6.83545 18.7579 6.99275C18.9562 7.15005 19.1037 7.36236 19.1819 7.6031C19.26 7.84383 19.2654 8.10228 19.1973 8.34606C19.1292 8.58984 18.9907 8.80811 18.7992 8.97352L18.8039 8.97274Z"
        fill={`url(#${id})`}
      />
    </svg>
  );
}

/**
 * Ratings block: "Ratings" heading, 5 partial-fill stars showing the
 * listing's average score + total count, plus the ratings-policy blurb.
 * Mirrors RN `ListingRatingInfoRow` + `ProductRatingView` (read-only —
 * submitting a rating is part of the deferred purchase/review flow).
 */
export function ProductRating({
  average,
  count,
}: {
  average: number;
  count: number;
}) {
  return (
    <section className="space-y-2.5 px-4">
      <h3 className="text-label font-bold leading-[18px] text-black">
        Ratings
      </h3>
      <div className="flex items-center gap-1">
        <div className="flex items-center gap-0.5">
          {[0, 1, 2, 3, 4].map(i => (
            <StarIcon key={i} value={Math.max(0, Math.min(1, average - i))} />
          ))}
        </div>
        <span className="text-label font-bold leading-[15px] text-black">{`(${count})`}</span>
      </div>
      <p className="text-[8px] font-medium leading-3 text-black">
        We only collect <span className="text-product-detail-accent">★</span>{' '}
        ratings due to the fact that most of our items sell out in 14 days or
        less. Customers can submit video reviews which are featured on the
        sellers profile.
      </p>
    </section>
  );
}
